import { useTranslation } from 'react-i18next';
import { RuBadges } from './RuBadges';
import type { ToolCard as ToolCardData } from '@/utils/api-pb';

interface ToolCardProps {
  tool: ToolCardData;
  why?: string;
  whyLoading?: boolean;
  onGo: (slug: string) => void;
}

export function Logo({ src, name, size = 48 }: { src: string | null; name: string; size?: number }) {
  return src ? (
    <img src={src} alt="" width={size} height={size} className="rounded-xl object-contain" data-tool-logo={name} />
  ) : (
    <div
      style={{ width: size, height: size }}
      className="flex items-center justify-center rounded-xl bg-[var(--grey-background,#f8f8f8)] font-manrope text-[18px] font-semibold text-[color:var(--grey-text,#565858)]"
    >
      {name.slice(0, 1)}
    </div>
  );
}

/**
 * Pricing label for the card. `free` always reads "Бесплатно" — but a `freemium` or
 * `paid` tool with no known RUB price must never say that (review finding I-2): a
 * freemium tool without a listed price gets "Есть бесплатный тариф", a paid one
 * without a price gets "Платно". When a price IS known, it's shown (with the
 * freemium label prefixed, since a free tier existing is still useful to know).
 */
export function priceLabel(tool: ToolCardData, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (tool.pricing_model === 'free') return t('free');
  const priceFrom = tool.price_from_rub !== null
    ? t('priceFrom', { value: tool.price_from_rub.toLocaleString('ru-RU') })
    : null;
  if (tool.pricing_model === 'freemium') {
    return priceFrom ? `${t('freemiumLabel')} · ${priceFrom}` : t('freemiumLabel');
  }
  return priceFrom ?? t('paidLabel');
}

/**
 * `verified_at` is a PocketBase date-only value (YYYY-MM-DD, no time component).
 * Formatting it through `new Date(...).toLocaleDateString()` would parse it as UTC
 * midnight and then render in the viewer's local timezone, which can roll the date
 * back a day west of UTC — so the digits are rearranged directly instead.
 */
function formatVerifiedDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}.${month}.${year}`;
}

export function ToolCard({ tool, why, whyLoading, onGo }: ToolCardProps) {
  const { t } = useTranslation('tools');
  const price = priceLabel(tool, t);

  return (
    <article
      className="flex flex-col gap-3 rounded-[24px] border border-[var(--grey-accent,#eeeeee)] bg-white p-5"
      data-slug={tool.slug}
    >
      {/* F-08.1 (стенд): длинная цена в шапке сжималась в узкий столбик рядом с процентом
          совпадения — теперь название и процент стоят в одной строке, а цена идёт отдельной
          строкой на всю ширину карточки. */}
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Logo src={tool.logoUrl} name={tool.name} />
          <h3 className="min-w-0 flex-1 truncate font-manrope text-[18px] font-semibold text-[color:var(--black-text,#0d0d0d)]">
            {tool.name}
          </h3>
          {/* Text darkened from --main-brand to clear WCAG AA (4.5:1) on the light badge
              background — see task-12-report.md for the measured ratio. The main CTA
              button below keeps --main-brand/#2197F1 as its background per constraints. */}
          <span className="shrink-0 rounded-full bg-[#EAF4FE] px-3 py-1 font-segoe text-[14px] font-semibold text-[#0B5FA5]">
            {t('match', { value: Math.round(tool.score * 100) })}
          </span>
        </div>
        <p className="line-clamp-2 font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
          {price}
          {tool.price_note ? ` · ${tool.price_note}` : ''}
        </p>
      </header>

      <p className="font-segoe text-[15px] text-[color:var(--black-text,#0d0d0d)]">{tool.description_short}</p>
      <RuBadges tool={tool} />

      {whyLoading && <div className="h-4 w-3/4 animate-pulse rounded bg-[var(--grey-background,#f8f8f8)]" aria-hidden />}
      {why && (
        <p className="font-segoe text-[14px] text-[color:var(--black-text,#0d0d0d)]">
          <span className="font-semibold">{t('why')}</span> {why}
        </p>
      )}

      {tool.aggregators.length > 0 && (
        <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
          {t('via')} {tool.aggregators.map((a) => a.name).join(', ')}
        </p>
      )}
      {tool.ru_alternatives.length > 0 && (
        <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
          {t('alternative')} {tool.ru_alternatives.map((a) => a.name).join(', ')}
        </p>
      )}

      <p className="font-segoe text-[12px] text-[color:var(--grey-text,#565858)]">
        {t('verified', { date: formatVerifiedDate(tool.verified_at) })}
      </p>

      <button
        type="button"
        onClick={() => onGo(tool.slug)}
        className="mt-auto h-[48px] rounded-[16px] bg-[#2197F1] px-5 font-segoe text-[16px] font-medium text-white transition-opacity hover:opacity-90"
      >
        {t('go')} →
      </button>
    </article>
  );
}
